import { useCallback, useEffect, useRef, useState } from "react";
import { Award, Loader2, AlertTriangle, CheckCircle2, X, BarChart3, ThumbsUp, Target, Lock, Coins, Info } from "lucide-react";
import api from "../../api";
import Portal from "../common/Portal";

// "Get feedback on this session": the paid quality review of a recorded call
// (backend clinical/notes/call_evaluation.py, feature "call_evaluation"). The
// same evaluation an uploaded script gets (scores, strengths, areas to
// improve), on the call's transcript. It never changes the clinician's note
// and never reaches the patient. Each review costs one AI session, so the
// window states the cost before the clinician presses the button that names it;
// a failed review, and the first re-run after the speakers were retagged, are free.
//
// While a review is running this re-checks every 30s (only then), and calls
// onReady() when it's done so the page can load the report.
//
// A small "Feedback" header button (dot: amber while running, green when ready)
// opening this in a window. Hidden until the session has a finished transcript.
export default function SessionFeedbackCard({ appointmentId, rolesKey, onReady }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const wasRunning = useRef(false);

  const load = useCallback(async () => {
    if (!appointmentId) return;
    try {
      const res = await api.getCallFeedback(appointmentId);
      setInfo(res.data);
    } catch {
      setInfo(null); // feature off (404) or no access: show nothing
    }
  }, [appointmentId]);

  useEffect(() => { load(); }, [load, rolesKey]);

  useEffect(() => {
    if (info?.state === "running") {
      wasRunning.current = true;
      const t = setTimeout(load, 30000);
      return () => clearTimeout(t);
    }
    if (info?.state === "done" && wasRunning.current) {
      wasRunning.current = false;
      onReady?.();
    }
    return undefined;
  }, [info, load, onReady]);

  if (!info?.enabled || info.blocker === "This session has no finished transcript.") return null;

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api.startCallFeedback(appointmentId);
      setInfo(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Couldn't start the feedback. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const { state, stale, free, blocker, cost, ai_paused: paused } = info;
  const canStart = !blocker && !paused && state !== "running" && (!info.reviewed || stale || state === "failed");
  const dot = state === "running" ? "bg-amber-400" : state === "done" && !stale ? "bg-emerald-500" : state === "failed" ? "bg-red-500" : null;
  const close = () => { setOpen(false); setError(""); };

  // One status panel at most: why it can't run, or where the last review is.
  let status = null;
  if (blocker) status = { tone: "amber", icon: Info, text: blocker };
  else if (paused) status = { tone: "amber", icon: Info, text: "AI is paused for your clinic until the AI dues are cleared in Finance." };
  else if (state === "running") status = { tone: "blue", icon: Loader2, spin: true, text: "Your feedback is being prepared. It usually takes a few minutes; you can close this window." };
  else if (state === "done" && !stale) status = { tone: "green", icon: CheckCircle2, text: "Your feedback is ready. Open it from the report download button at the top." };
  else if (state === "done" && stale) status = { tone: "amber", icon: AlertTriangle, text: "The speakers or the transcript changed after this feedback was made." };
  else if (state === "failed") status = { tone: "red", icon: AlertTriangle, text: "The last attempt didn't work. Trying again is free." };
  const TONE = {
    amber: "border-amber-200 bg-amber-50 text-amber-900", blue: "border-blue-200 bg-blue-50 text-blue-900",
    green: "border-emerald-200 bg-emerald-50 text-emerald-900", red: "border-red-200 bg-red-50 text-red-900",
  };
  const action = !info.reviewed ? "Get feedback" : state === "failed" ? "Try again" : "Get feedback again";

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title="Session feedback for you"
        className="relative inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-600 shadow-sm transition-all hover:bg-primary-50">
        <Award className="h-3.5 w-3.5" />
        Feedback
        {dot && <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />}
      </button>

      {open && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-labelledby="session-feedback-title"
            onKeyDown={(e) => e.key === "Escape" && close()}>
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start gap-3 px-5 pb-3 pt-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                  <Award className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 id="session-feedback-title" className="text-base font-bold text-secondary-900">Session feedback</h2>
                  <p className="text-xs text-secondary-500">A review of your work in this session</p>
                </div>
                <button type="button" onClick={close} aria-label="Close" className="-mr-1 -mt-1 rounded-full p-1.5 text-secondary-500 hover:bg-secondary-100">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-4 px-5 pb-5">
                <p className="text-sm text-secondary-700">An AI review of how this session went, from its transcript.</p>
                <ul className="space-y-2 text-sm text-secondary-800">
                  <li className="flex items-start gap-2.5"><BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" /> Scores for each therapy skill</li>
                  <li className="flex items-start gap-2.5"><ThumbsUp className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" /> What went well, with moments from the session</li>
                  <li className="flex items-start gap-2.5"><Target className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" /> What to work on, with suggestions</li>
                </ul>
                <p className="flex items-start gap-2 text-xs text-secondary-500">
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Your note isn't changed, and the patient never sees this.
                </p>

                {status && (
                  <div className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${TONE[status.tone]}`} role="status">
                    <status.icon className={`mt-0.5 h-4 w-4 shrink-0 ${status.spin ? "animate-spin" : ""}`} />
                    <span>{status.text}</span>
                  </div>
                )}

                {canStart && (
                  <div className="flex items-start gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-sm text-secondary-800">
                    <Coins className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />
                    <span>{free ? <>This one is <strong>free</strong>.</> : <>This uses <strong>{cost}</strong>, on top of the session's transcript.</>}</span>
                  </div>
                )}
                {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
              </div>

              <div className="flex justify-end gap-2 border-t border-secondary-100 bg-secondary-50/60 px-5 py-3">
                <button type="button" onClick={close}
                  className="rounded-lg border border-secondary-300 bg-white px-3.5 py-2 text-sm font-semibold text-secondary-700 hover:bg-secondary-50">
                  {canStart ? "Cancel" : "Close"}
                </button>
                {canStart && (
                  <button type="button" onClick={start} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-bold text-white hover:bg-primary-700 disabled:opacity-60">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {action}{free ? " (free)" : ` · ${cost}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
